import {
  CircleUserRound,
  Mail,
  Menu,
  Pencil,
  Phone,
  ShoppingCart,
  User,
  X,
} from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { useCart } from '../../context/CartContext';
import { clearSession, getSession } from '../../utils/authStorage';
import CartDrawer from '../CartDrawer/CartDrawer';
import './Header.css';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAccountPopupOpen, setIsAccountPopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [isLoadingMe, setIsLoadingMe] = useState(false);
  const [meError, setMeError] = useState('');
  const [meData, setMeData] = useState(null);
  const [editError, setEditError] = useState('');
  const [editingField, setEditingField] = useState(null); // 'fullName' | 'phone' | null
  const [editForm, setEditForm] = useState({
    fullName: '',
    phone: '',
  });
  const navigate = useNavigate();

  function splitFullName(full) {
    const parts = (full || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return { first_name: '', last_name: '' };
    return {
      first_name: parts[0],
      last_name: parts.slice(1).join(' '),
    };
  }

  const activeLang = i18n.language?.startsWith('en') ? 'en' : 'vi';
  const logoSrc = '/logo_aura_glow.png';

  const switchLang = (lang) => {
    if (lang === activeLang) return;
    i18n.changeLanguage(lang);
  };
  const {
    items,
    isOpen: isCartOpen,
    openCart,
    closeCart,
    clearCart,
    itemCount,
    subtotal,
    updateQuantity,
    removeItem,
  } = useCart();
  const location = useLocation();

  const handleUserClick = async () => {
    const { access } = getSession();
    if (!access) {
      navigate('/login');
      return;
    }

    if (isAccountPopupOpen) {
      setIsAccountPopupOpen(false);
      return;
    }

    setIsLoadingMe(true);
    setMeError('');
    try {
      const { data } = await api.get('/api/auth/me/');
      setMeData(data);
      setIsAccountPopupOpen(true);
    } catch (err) {
      setMeError('Không tải được thông tin tài khoản.');
      setIsAccountPopupOpen(true);
    } finally {
      setIsLoadingMe(false);
    }
  };

  const handleLogout = () => {
    clearSession();
    setIsAccountPopupOpen(false);
    setIsEditPopupOpen(false);
    setMeData(null);
    navigate('/login');
  };

  const handleEditOpen = () => {
    if (!meData) return;
    setIsAccountPopupOpen(false);
    setIsEditPopupOpen(true);
    setEditError('');
    setEditingField(null);
    setEditForm({
      fullName: fullName,
      phone: meData?.phone || '',
    });
  };

  const handleEditCancel = () => {
    setIsEditPopupOpen(false);
    setEditError('');
    setEditingField(null);
    setIsAccountPopupOpen(true);
  };

  const handleEditSave = async () => {
    if (!meData) return;
    setEditError('');
    try {
      const { first_name, last_name } = splitFullName(editForm.fullName);
      const payload = {
        first_name,
        last_name,
        phone: (editForm.phone || '').trim(),
      };

      const res = await api.patch('/api/auth/me/', payload);
      setMeData(res?.data || {});
      setIsEditPopupOpen(false);
      setEditingField(null);
      setIsAccountPopupOpen(true);
    } catch (err) {
      setEditError(t('account.editError'));
    }
  };

  const fullName =
    [meData?.first_name, meData?.last_name].filter(Boolean).join(' ').trim() ||
    meData?.username ||
    'User';
  const accountInfoItems = [
    {
      key: 'fullname',
      icon: <CircleUserRound size={19} />,
      label: t('account.fullName'),
      value: fullName,
    },
    {
      key: 'username',
      icon: <User size={19} />,
      label: t('account.username'),
      value: meData?.username || '-',
    },
    {
      key: 'email',
      icon: <Mail size={19} />,
      label: t('account.email'),
      value: meData?.email || '-',
    },
    {
      key: 'phone',
      icon: <Phone size={19} />,
      label: t('account.phone'),
      value: meData?.phone || '-',
    },
  ];

  return (
    <header className="header">
      <div className="container header-container">
        <button className="mobile-menu-btn" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        <div className="logo">
          <Link to="/"><img className="logo-img" src={logoSrc} alt="AuraGlowDerma" /></Link>
        </div>

        <nav className={`nav-menu ${isMobileMenuOpen ? 'active' : ''}`}>
          <ul>
            <li>
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)}
                className={location.pathname === '/' ? 'nav-link active' : 'nav-link'}>
                {t('header.home')}
              </Link>
            </li>
            <li>
              <Link to="/best-seller" onClick={() => setIsMobileMenuOpen(false)}
                className={location.pathname === '/best-seller' ? 'nav-link active' : 'nav-link'}>
                {t('header.bestSeller')}
              </Link>
            </li>
            <li>
              <Link to="/our-story" onClick={() => setIsMobileMenuOpen(false)}
                className={location.pathname === '/our-story' ? 'nav-link active' : 'nav-link'}>
                {t('header.ourStory')}
              </Link>
            </li>
          </ul>
        </nav>

        <div className="header-icons">
          {/* Nút chuyển ngôn ngữ */}
          <div className="lang-switcher notranslate" translate="no">
            <button
              className={`lang-btn ${activeLang === 'vi' ? 'active' : ''}`}
              onClick={() => switchLang('vi')}
            >VI</button>
            <span className="lang-sep">|</span>
            <button
              className={`lang-btn ${activeLang === 'en' ? 'active' : ''}`}
              onClick={() => switchLang('en')}
            >EN</button>
          </div>


          <button className="icon-btn" onClick={handleUserClick} aria-label="User menu">
            <User size={20} />
          </button>
          <button className="icon-btn cart-btn" onClick={openCart} aria-label="Mở giỏ hàng">
            <ShoppingCart size={20} />
            <span className="cart-count">{itemCount}</span>
          </button>
        </div>
      </div>

      {isAccountPopupOpen && (
        <div className="account-popup-overlay" onClick={() => setIsAccountPopupOpen(false)}>
          <div className="account-popup" onClick={(e) => e.stopPropagation()}>
            <button
              className="account-popup-close"
              onClick={() => setIsAccountPopupOpen(false)}
              aria-label="Close account popup"
            >
              <X size={18} />
            </button>

            <button
              className="account-popup-edit-btn"
              onClick={handleEditOpen}
              aria-label="Edit account"
              type="button"
            >
              <Pencil size={16} />
            </button>

            {isLoadingMe ? (
              <p className="account-popup-loading">{t('account.loading')}</p>
            ) : meError ? (
              <p className="account-popup-error">{meError}</p>
            ) : (
              <>
                <div className="account-popup-hero">
                  <div className="account-popup-avatar">
                    <CircleUserRound size={28} />
                  </div>
                  <div className="account-popup-hero-text">
                    <h3 className="account-popup-title">{t('account.greeting', { name: fullName })}</h3>
                    <p className="account-popup-subtitle">{t('account.subtitle')}</p>
                  </div>
                </div>
                <div className="account-popup-menu">
                  {accountInfoItems.map((item) => (
                    <div key={item.key} className="account-popup-item">
                      <span className="account-popup-item-icon">{item.icon}</span>
                      <span className="account-popup-item-content">
                        <span className="account-popup-item-line">
                          <strong>{item.label}:</strong> {item.value}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <button className="account-popup-logout" onClick={handleLogout}>
              {t('account.signOut')}
            </button>
          </div>
        </div>
      )}

      {isEditPopupOpen && (
        <div className="account-popup-overlay" onClick={handleEditCancel}>
          <div className="account-popup account-edit-popup" onClick={(e) => e.stopPropagation()}>
            <button className="account-popup-close" onClick={handleEditCancel} aria-label="Close edit popup">
              <X size={18} />
            </button>

            <h3 className="account-edit-title">{t('account.editTitle')}</h3>
            {editError ? <p className="account-edit-error">{editError}</p> : null}

            <div className="account-edit-rows">
              <div className="account-edit-row">
                <div className="account-edit-row-left">
                  <span className="account-popup-item-icon">{/* icon */ <CircleUserRound size={18} />}</span>
                  <div className="account-edit-row-text">
                    <strong>{t('account.fullName')}:</strong>{' '}
                    {editingField === 'fullName' ? (
                      <input
                        className="account-edit-input"
                        value={editForm.fullName}
                        onChange={(e) => setEditForm((p) => ({ ...p, fullName: e.target.value }))}
                        autoFocus
                      />
                    ) : (
                      <span>{editForm.fullName}</span>
                    )}
                  </div>
                </div>
                {editingField !== 'fullName' ? (
                  <button
                    type="button"
                    className="account-edit-pencil-btn"
                    onClick={() => setEditingField('fullName')}
                    aria-label="Edit full name"
                  >
                    <Pencil size={16} />
                  </button>
                ) : null}
              </div>

              <div className="account-edit-row">
                <div className="account-edit-row-left">
                  <span className="account-popup-item-icon">{/* icon */ <User size={18} />}</span>
                  <div className="account-edit-row-text">
                    <strong>{t('account.username')}:</strong> <span>{meData?.username || '-'}</span>
                  </div>
                </div>
              </div>

              <div className="account-edit-row">
                <div className="account-edit-row-left">
                  <span className="account-popup-item-icon">{/* icon */ <Mail size={18} />}</span>
                  <div className="account-edit-row-text">
                    <strong>Email:</strong> <span>{meData?.email || '-'}</span>
                  </div>
                </div>
              </div>

              <div className="account-edit-row">
                <div className="account-edit-row-left">
                  <span className="account-popup-item-icon">{/* icon */ <Phone size={18} />}</span>
                  <div className="account-edit-row-text">
                    <strong>{t('account.phone')}:</strong>{' '}
                    {editingField === 'phone' ? (
                      <input
                        className="account-edit-input"
                        value={editForm.phone}
                        onChange={(e) => setEditForm((p) => ({ ...p, phone: e.target.value }))}
                        inputMode="tel"
                        autoFocus
                      />
                    ) : (
                      <span>{editForm.phone || '-'}</span>
                    )}
                  </div>
                </div>
                {editingField !== 'phone' ? (
                  <button
                    type="button"
                    className="account-edit-pencil-btn"
                    onClick={() => setEditingField('phone')}
                    aria-label="Edit phone"
                  >
                    <Pencil size={16} />
                  </button>
                ) : null}
              </div>
            </div>

            <div className="account-edit-actions">
              <button className="account-edit-cancel-btn" type="button" onClick={handleEditCancel}>
                {t('account.cancel')}
              </button>
              <button className="account-edit-save-btn" type="button" onClick={handleEditSave}>
                {t('account.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      <CartDrawer
        isOpen={isCartOpen}
        onClose={closeCart}
        items={items}
        subtotal={subtotal}
        updateQuantity={updateQuantity}
        removeItem={removeItem}
        itemCount={itemCount}
        clearCart={clearCart}
      />
    </header>
  );
};

export default Header;
